import React, { useEffect, useState } from "react";
import api from "@/api";
import { Plus, Send, Copy, Check, Trash2, X, Edit2, AlertCircle } from "lucide-react";
import { copyToClipboard } from "@/utils/clipboard";

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700",
  uploaded: "bg-[#E5F0F2] text-[#006080]",
  overdue: "bg-red-50 text-red-700",
  cancelled: "bg-slate-100 text-slate-500",
};

function NewRequestModal({ schemas, onClose, onSave }) {
  const [form, setForm] = useState({ title: "", description: "", schema_id: "", recipient_name: "", recipient_email: "", expires_days: 7, link_type: "dev" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/document-requests", form);
      setCreated(r.data);
    } catch (err) {} finally { setSaving(false); }
  };

  const uploadLink = created ? `${window.location.origin}/r/${created.token}` : "";
  const copyLink = () => { copyToClipboard(uploadLink); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  if (created) return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200"><h3 className="font-bold text-slate-900">Request Created</h3></div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">Share this link with <strong>{form.recipient_name || "your recipient"}</strong> to upload their document:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono bg-slate-50 border border-slate-200 rounded px-3 py-2 overflow-x-auto break-all">{uploadLink}</code>
            <button onClick={copyLink} data-testid="copy-request-link" className="p-2 hover:bg-slate-100 rounded flex-shrink-0">
              {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} className="text-slate-400" />}
            </button>
          </div>
          <button onClick={() => { onSave(); }} className="w-full h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66]">Done</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md animate-fade-in overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200"><h3 className="font-bold text-slate-900">New Document Request</h3></div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Title</label>
            <input data-testid="request-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="e.g., Q1 VAT Receipts" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Description (optional)</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Schema (optional)</label>
            <select value={form.schema_id} onChange={e => setForm(f => ({ ...f, schema_id: e.target.value }))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
              <option value="">No schema</option>
              {schemas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-2">Link Type</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "dev", label: "Dev", desc: "Single use, expires after first upload" },
                { value: "production", label: "Production", desc: "Reusable, shows extracted fields instantly" },
              ].map(opt => (
                <button key={opt.value} type="button" data-testid={`link-type-${opt.value}`}
                  onClick={() => setForm(f => ({ ...f, link_type: opt.value }))}
                  className={`p-3 text-left border rounded-md transition-colors ${form.link_type === opt.value ? "border-[#006080] bg-[#E5F0F2]" : "border-slate-200 hover:border-slate-300"}`}>
                  <p className={`text-xs font-bold ${form.link_type === opt.value ? "text-[#006080]" : "text-slate-700"}`}>{opt.label}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Recipient Name</label>
              <input value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Expires (days)</label>
              <input type="number" value={form.expires_days} onChange={e => setForm(f => ({ ...f, expires_days: parseInt(e.target.value) || 7 }))} min={1} max={30} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="create-request-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Creating..." : "Create Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditRequestModal({ request, schemas, onClose, onSave }) {
  const [form, setForm] = useState({
    title: request.title || "",
    description: request.description || "",
    schema_id: request.schema_id || "",
    recipient_name: request.recipient_name || "",
    recipient_email: request.recipient_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.put(`/document-requests/${request.id}`, form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update request");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md animate-fade-in overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200"><h3 className="font-bold text-slate-900">Edit Request</h3></div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Title</label>
            <input data-testid="edit-request-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Schema</label>
            <select value={form.schema_id} onChange={e => setForm(f => ({ ...f, schema_id: e.target.value }))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
              <option value="">No schema</option>
              {schemas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Recipient Name</label>
            <input value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="save-edit-request-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DocumentRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [copiedToken, setCopiedToken] = useState(null);
  const [actionError, setActionError] = useState("");

  const fetchAll = () => {
    setLoading(true);
    Promise.all([api.get("/document-requests"), api.get("/schemas")]).then(([r, s]) => {
      setRequests(r.data);
      setSchemas(s.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCancel = async (id) => {
    try {
      await api.put(`/document-requests/${id}/cancel`);
      fetchAll();
    } catch (err) {
      setActionError("Failed to cancel request");
      setTimeout(() => setActionError(""), 3000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this request?")) return;
    setActionError("");
    try {
      await api.delete(`/document-requests/${id}`);
      fetchAll();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to delete request");
      setTimeout(() => setActionError(""), 3000);
    }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/r/${token}`;
    copyToClipboard(link);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Document Requests</h2>
          <p className="text-sm text-slate-500">Request documents from clients via secure tokenized links.</p>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="create-request-modal-button" className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors">
          <Plus size={14} /> New Request
        </button>
      </div>

      {actionError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} className="flex-shrink-0" /> {actionError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
          <Send size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-3">No document requests yet</p>
          <button onClick={() => setShowModal(true)} className="h-9 px-4 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66]">Create first request</button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["Title", "Recipient", "Schema", "Status", "Expires", ""].map(h => (
                  <th key={h} className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((req, i) => {
                const schema = schemas.find(s => s.id === req.schema_id);
                return (
                  <tr key={req.id} data-testid={`request-row-${req.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{req.title}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${req.link_type === "production" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {req.link_type === "production" ? "Prod" : "Dev"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{req.recipient_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{schema?.name || "—"}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[req.status] || "bg-slate-100 text-slate-600"}`}>{req.status}</span></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{req.expires_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {req.status === "pending" && (
                          <>
                            <button onClick={() => copyLink(req.token)} data-testid={`copy-link-${req.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-[#006080]" title="Copy upload link">
                              {copiedToken === req.token ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                            </button>
                            <button onClick={() => setEditingRequest(req)} data-testid={`edit-request-${req.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600" title="Edit request">
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => handleCancel(req.id)} data-testid={`cancel-request-${req.id}`} className="p-1.5 hover:bg-amber-50 rounded text-slate-400 hover:text-amber-600" title="Cancel request">
                              <X size={13} />
                            </button>
                          </>
                        )}
                        <button onClick={() => handleDelete(req.id)} data-testid={`delete-request-${req.id}`} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500" title="Delete permanently">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <NewRequestModal schemas={schemas} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); fetchAll(); }} />}
      {editingRequest && <EditRequestModal request={editingRequest} schemas={schemas} onClose={() => setEditingRequest(null)} onSave={() => { setEditingRequest(null); fetchAll(); }} />}
    </div>
  );
}
